import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import RewardCard from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/RewardCard';
import BICashContent from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/BICash/BICashContent';
import localeUtils from 'utils/LanguageLocale';
import BeautyInsiderRewardsSummaryEN from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/locales/BeautyInsiderRewardsSummary_en_US';
import BeautyInsiderRewardsSummaryFR from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/locales/BeautyInsiderRewardsSummary_fr_CA';
import promoUtils from 'utils/Promos';
const { CTA_TYPES, applyPromo, removePromo } = promoUtils;
import rougeUtils from 'utils/rougeExclusive';
import analyticsUtils from 'analytics/utils';
import analyticsConstants from 'analytics/constants';
import urlUtils from 'utils/Url';
import { globalModals as globalModalsConst, renderModal } from 'utils/globalModals';
const { BEAUTY_INSIDER_CASH_INFO } = globalModalsConst;
import Empty from 'constants/empty';

const {
    LinkData: { BI_CASH_APPLY }
} = analyticsConstants;

// The $5 BI Cash denomination does not have a "Learn More" modal
const FIVE_DOLLAR_BI_CASH = 5;

/**
 * Renders the Beauty Insider Cash reward card with apply/remove CTA logic.
 * Handles single and multiple promotion scenarios, Rouge exclusive badge display,
 * and delegates to the multiple promos modal when eligibleCBRCount > 1.
 * @param {Object} props
 * @param {Object} props.cbr - CBR data for the selected basket: { promos, biPoints, grossBeautyBankPoints, error }
 * @param {boolean} props.isDualBasket - Whether the user has a dual (store + online) basket
 * @param {Function} props.onOpenModal - Callback to open the multiple promos modal
 * @param {Object} props.globalModals - Global modal definitions provided by withGlobalModals HOC
 */
const BICash = props => {
    const {
        isDualBasket,
        cbr,
        onOpenModal,
        globalModals: globalModalsData
    } = props;

    const { promos } = cbr;
    const sortedPromotions = sortPromos(promos);
    const appliedCBRPromotion = cbr?.promos?.find(promo => promo.isApplied);
    const isBiCashApplied = !!appliedCBRPromotion;
    const displayedPromotion = appliedCBRPromotion || getDisplayedPromotion(sortedPromotions);
    const { ctaLabel, ctaAction } = isBiCashApplied ? Empty.Object : getBiCashCta(
        isDualBasket,
        sortedPromotions.length,
        displayedPromotion,
        onOpenModal
    );


    const removeFromBasket = e => {
        e.stopPropagation();
        removePromo(appliedCBRPromotion?.couponCode.toLowerCase(), CTA_TYPES[appliedCBRPromotion?.promotionType]);
    };
    const shouldShowLearnMore = displayedPromotion?.discountAmount !== FIVE_DOLLAR_BI_CASH;

    const leftContent = (
        <BICashContent
            promotions={sortedPromotions}
            displayedPromotion={displayedPromotion}
            appliedDiscountAmount={appliedCBRPromotion?.discountAmount}
            applied={isBiCashApplied}
        />
    );

    return (
        <RewardCard
            buttonText={ctaLabel}
            linkText={shouldShowLearnMore ? getText('biCashLinkText') : null}
            showRougeBadge={rougeUtils.renderRougeExclusiveBadge(cbr.grossBeautyBankPoints)}
            onButtonClick={ctaAction}
            onRemoveClick={removeFromBasket}
            onLinkClick={() => renderModal(globalModalsData[BEAUTY_INSIDER_CASH_INFO])}
            leftContent={leftContent}
            applied={isBiCashApplied}
            disabled={promos.length === 1 && !promos[0]?.isEligible && !promos[0]?.isApplied}
        />
    );
};

const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderRewardsSummaryEN, BeautyInsiderRewardsSummaryFR);

/**
 * Determines the button text and action for Beauty Insider Cash based on basket state and promotions
 * @param {boolean} isDualBasket - Whether the user has a dual basket
 * @param {number} availableCBRCount - Number of available CBR promotions
 * @param {Object} displayedPromotion - The promotion to display ({ couponCode, discountAmount, promotionType })
 * @param {Function} openModal - Callback to open the multiple promos modal
 * @returns {{ ctaLabel: string, ctaAction: Function }} The appropriate button label and click handler
 */
const getBiCashCta = (isDualBasket, availableCBRCount, displayedPromotion, openModal) => {
    if (isDualBasket) {
        return {
            ctaLabel: getText('applyInBasketButtonText'),
            ctaAction: () => {
                analyticsUtils.setNextPageData({ linkData: BI_CASH_APPLY });
                urlUtils.redirectTo('/basket');
            }
        };
    }

    if (availableCBRCount === 1) {
        return {
            ctaLabel: getText('applyButtonText'),
            ctaAction: () => applyPromo(displayedPromotion?.couponCode.toLowerCase(), null, CTA_TYPES[displayedPromotion?.promotionType])
        };
    }

    return {
        ctaLabel: getText('biCashButtonTextChoose'),
        ctaAction: openModal || (() => { })
    };
};

/**
 * Sorts promotions by ascending value (lowest first)
 * @param {Array} promotions - Array of promotion objects with { points }
 * @returns {Array} New sorted array
 */
const sortPromos = promotions =>
    promotions.slice().sort((a, b) => {
        return a.points - b.points;
    });

/**
 * Gets the displayed promotion and whether the Rouge badge should be shown.
 * Expects promotions to be pre-sorted by ascending value via sortPromos.
 * @param {Array} promotions - Pre-sorted array of promotion objects
 * @returns {Object|null} The displayed promotion object or null if none
 */
const getDisplayedPromotion = (promotions) => {
    if (!promotions || promotions.length === 0) {
        return null;
    }

    if (promotions.length === 1) {
        return promotions[0];
    }

    if (promotions.length === 2) {
        return null;
    }

    return promotions[promotions.length - 1];
};

export default wrapFunctionalComponent(BICash, 'BICash');
